import { createApi } from "@reduxjs/toolkit/query/react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import { mapWithConcurrency } from "../../utils/concurrency";
import { invalidateOfferAccountCaches } from "./offerAccountCache";

/**
 * エスクローが連動する（= 残高が変動する）スレッド状態。
 * この状態での `contract_amount` 編集は escrow_lock / escrow_decrease を伴う。
 */
const ESCROW_ACTIVE_STATUSES = new Set<string>([
  "in_progress",
  "inspection_pending",
]);

// ユーザーオブジェクト（subcontract のみ）
export interface DmThreadUser {
  id: number;
  display_name: string;
  avatar_url: string;
}

// 契約当事者オブジェクト
export interface DmSignatory {
  name: string | null;
  company: string | null;
  title: string | null;
  postal_code: string | null;
  prefecture: string | null;
  address1: string | null;
  address2: string | null;
}

/**
 * ステータス遷移日時。`thread_type` 横断で常に同じ 7 キーが返り、
 * 適用外フィールドは `null` で埋められる（仕様書 dm-threads.md §3-1 / §9 参照）。
 *
 * - subcontract: `inspected_at` は旧 `checked_at` の正規化（DB ソースは `OfferCreator.check_time`）。
 *   `completed_at` / `closed_at` は subcontract に専用カラムが無いため常に `null`。
 * - master_contract: `rejected_at` / `released_at` は対応する遷移が無いため常に `null`。
 */
export interface DmStatusTimestamps {
  accepted_at: string | null;
  contracted_at: string | null;
  inspected_at: string | null;
  rejected_at: string | null;
  released_at: string | null;
  completed_at: string | null;
  closed_at: string | null;
}

/**
 * 契約更新の差分に載せられる API フィールド名。BE の
 * `DmThreadApiView.AMENDABLE_API_FIELDS` と対応する。
 */
export type DmAmendableField =
  | "assigned_roles"
  | "scope"
  | "initial_message"
  | "contract_amount"
  | "compensation_lines"
  | "contract_start_date"
  | "contract_end_date"
  | "deadline"
  | "quantity"
  | "deliverable_format"
  | "delivery_method"
  | "delivery_location"
  | "special_provisions";

/**
 * 1 フィールドぶんの変更前後。値は BE が JSON 化した正規形で、
 * 金額は税抜の文字列（例 `"150000.00"`）、日付は ISO 文字列、
 * `compensation_lines` は対価行の配列になる。未設定は `null`。
 */
export interface DmAmendmentChange {
  previous: string | ApiCompensationLine[] | null;
  new: string | ApiCompensationLine[] | null;
}

/** 承認待ちの契約更新提案。 */
export interface DmPendingAmendment {
  changes: Partial<Record<DmAmendableField, DmAmendmentChange>>;
  /** 提案した委託者の user id */
  proposed_by: number | null;
  proposed_at: string | null;
}

export type DmThreadStatus =
  // subcontract のドラフト。BE は ``contract-status-flow.md`` S1 早見表に揃えて
  // ``'draft'`` を返すが、旧 API 値の ``'new'`` も後方互換のため受け付ける。
  | "draft"
  | "new"
  | "estimating"
  | "estimate_review"
  | "contractor_pending"
  | "rejected"
  | "in_progress"
  | "inspection_pending"
  | "closed"
  | "completed";

export type DmThreadType = "subcontract" | "master_contract";

/**
 * オファー成立時に受託者の `ProductUser.position` を書き換える値。
 *
 * 仕様書 §3-1-7 / §5 より:
 * - `PRODUCER` / `DIRECTOR` を付与する場合は再委託権限・顧客コミュニケーション権限を**暗黙的に内包**する
 *   （`allow_subcontracting` 個別値より上位の権限フラグとして振る舞う）
 * - `SPECIALIST` はスペシャリスト系の通常付与
 * - `null` は「変更なし」（master_contract は対応カラム未実装のため常に `null`）
 *
 * 書き込み時は `null` または `''`（空文字列）が等価で「変更なし」を意味し、
 * 読み込み時は常に `null` で返ってくる仕様（BE 側で正規化）。
 */
export type AcceptPosition = "PRODUCER" | "DIRECTOR" | "SPECIALIST";

/** master_contract の `offer_form.contract_type` 値域（OfferProduct.CONTRACT_TYPE 由来）。 */
export type MasterContractContractType = "buyout" | string;

/** master_contract の `offer_form.ownership_type` 値域。 */
export type MasterContractOwnershipType =
  | "provide_from_us"
  | "provide_from_client";

/** master_contract の `offer_form.disclosure_rule` 値域。 */
export type MasterContractDisclosureRule = "can_public" | "discuss_later";

export interface DmThread {
  // identity
  id: string;
  thread_type: DmThreadType;

  // participants
  /** subcontract のみ。master_contract は常に `null` */
  requester: DmThreadUser | null;
  /** subcontract のみ。master_contract は常に `null` */
  contractor: DmThreadUser | null;
  requester_signatory: DmSignatory | null;
  contractor_signatory: DmSignatory | null;

  // state
  status: DmThreadStatus;
  status_timestamps: DmStatusTimestamps;

  // scope
  assigned_roles: string | null;
  scope: string;

  // amounts
  contract_amount: number | null;
  /** 着手金率（0–100）。受諾時に `contract_amount * rate%` を先払い */
  advance_payment_rate: number;

  // schedule
  contract_start_date: string | null;
  contract_end_date: string | null;
  deadline: string | null;

  // workflow flags
  /**
   * 契約・支払いをプラットフォーム上で管理するスレッドか（既定 `true`）。
   * `false` のときはコミュニケーション専用スレッドで、契約締結ワークフローおよび
   * `PaymentRequest` ドメインを使用しない。
   */
  is_transactional: boolean;
  requires_deliverable: boolean;
  /**
   * 再委託許可フラグ。
   * subcontract: `OfferCreator.allow_subcontracting` を返す。
   * master_contract: `OfferProduct` に対応カラム未実装のため当面 `true` 固定。
   */
  allow_subcontracting: boolean;
  /**
   * オファー成立時に受託者へ付与するポジション（subcontract のみ）。
   * master_contract は常に `null`。
   */
  accept_position: AcceptPosition | null;
  /**
   * このスレッドの contractor が同一プロジェクトで「進行中の」再委託オファー
   * （子オファー）の admin/requester になっているかどうか。
   *
   * 親オファーを受諾した contractor が再委託（子オファー）を進めている最中に、
   * 親側で escrow を巻き戻す操作（クローズ相談・合意減額）を行うと子オファーの
   * 原資（``balance_committed_incoming`` 由来の lock）と整合が取れなくなる。
   * 完全な cascade-guard が実装されるまでの暫定対応として、FE はこのフラグが
   * `true` のとき親側の終了系 UI（「クローズを相談」など）を抑止する。
   *
   * master_contract / contractor 未確定（メール経由ドラフト等）の場合は常に `false`。
   */
  has_active_child_subcontracts: boolean;

  /**
   * 承認待ちの契約更新提案。提案が無ければ `null`。
   *
   * 締結後（`in_progress` / `inspection_pending`）の契約条件は PATCH では
   * 変更できず、委託者が `amendment-request` で提案し、受託者が
   * `amendment-response` で承認して初めて反映される。承認されるまで
   * `thread` 本体の値は更新前のまま。
   *
   * subcontract のみ。master_contract は常に `null`。
   */
  pending_amendment: DmPendingAmendment | null;

  // engagement
  /** 初回メッセージ草稿（送信遷移時に user メッセージとして自動投入される） */
  initial_message: string | null;
  unread_count: number;

  // audit
  created_at: string;
  updated_at: string;
}

export interface DmThreadsData {
  threads: DmThread[];
  count: number;
  has_next: boolean;
  next_offset: number | null;
  limit: number;
  offset: number;
  type: string;
  status_filter: string | null;
  role: string | null;
  search: string | null;
  sort_by: string;
  order: string;
}

export interface DmThreadsResponse {
  status: string;
  data: DmThreadsData;
}

// ───── Detail API 拡張ブロック（仕様書 §3-2） ─────

/**
 * 対価行（仕様書 2026-05-09 追加）。
 *
 * `Role.compensation_lines` と完全同形式で、`offer_form.compensation_lines` に配置される。
 * 配列の挿入順 = 表示順（並び順を持つ専用キーは無い）。
 *
 * BE は `unit_price` / `quantity` を **常に文字列** で返却する（精度欠落回避のため）。
 * FE で計算する際は `Number()` または `parseFloat` で数値化する。
 *
 * `compensation_lines` が non-empty で PATCH すると、サーバ側で
 * `unit_price * quantity` の総和を自動計算して `thread.contract_amount` に上書きする。
 * 空配列の場合は従来通り `contract_amount` を直接管理する。
 */
export interface ApiCompensationLine {
  label: string;
  /** 単価（Decimal 文字列）。Read 時は常に文字列、Write 時は文字列・数値どちらも可 */
  unit_price: string;
  /** 数量（Decimal 文字列）。Read 時は常に文字列、Write 時は文字列・数値どちらも可 */
  quantity: string;
  /** 単位（"式" / "本" / "時間" / "ヶ月" 等の自由記述。空文字許容） */
  unit: string;
}

/** subcontract の `offer_form` バリアント（仕様書 §3-2）。 */
export interface SubcontractOfferForm {
  initial_message: string | null;
  special_provisions: string | null;
  quantity: string | null;
  deliverable_format: string | null;
  delivery_method: string | null;
  delivery_location: string | null;
  valid_until: string | null;
  /**
   * メール経由オファーの宛先メール（受託者が未登録 / 確定前のスナップショット）。
   * 通常オファーの場合は `null`。subcontract のみ。
   */
  recipient_email: string | null;
  /** 対価の内訳。配列順 = 表示順。 */
  compensation_lines: ApiCompensationLine[];
}

/** master_contract の `offer_form` バリアント（仕様書 §3-2）。 */
export interface MasterContractOfferForm {
  initial_message: string | null;
  /** 提示時の予算枠（旧名 `budget`）。`thread.contract_amount` とは別概念 */
  proposed_budget: number | null;
  contract_type: MasterContractContractType;
  ownership_type: MasterContractOwnershipType;
  disclosure_rule: MasterContractDisclosureRule;
  /** 対価の内訳。配列順 = 表示順。 */
  compensation_lines: ApiCompensationLine[];
}

export type DmOfferForm = SubcontractOfferForm | MasterContractOfferForm;

/** 初回オファーに添付されたファイル */
export interface DmInitialFile {
  /** ファイル ID。subcontract: `CreatorOfferFile.pk` / master_contract: `OfferProduct.pk` */
  id: string | null;
  url: string | null;
  file_name: string | null;
}

/** 契約書ファイルの `kind` 値域（仕様書 §3-2）。 */
export type DmContractFileKind =
  | "draft_active"
  | "draft_expired"
  | "draft_superseded"
  | "draft_rejected"
  | "concluded";

export interface DmContractFile {
  /** ファイル ID。subcontract のレガシースロット由来は `null` */
  id: string | null;
  kind: DmContractFileKind;
  url: string | null;
  /** ダウンロード用の安定したファイル名（ストレージのサフィックスを含まない） */
  filename: string | null;
  uploaded_at: string | null;
  /** subcontract のレガシースロットでは `null` */
  uploaded_by: { id: number; display_name: string } | null;
  is_latest: boolean;
}

/**
 * 契約書ファイル一覧から、表示すべき最新ファイルの URL とファイル名を取り出す。
 * BE が `is_latest`（締結後は concluded、締結前/辞退は draft_active）を立てるため
 * それを優先し、無ければ末尾を使う。URL を持つものが無ければ `null`。
 */
export const selectLatestContractFile = (
  files: DmContractFile[] | undefined,
): { url: string; filename: string | null } | null => {
  if (!files || files.length === 0) return null;
  const latest = files.find((f) => f.is_latest) ?? files[files.length - 1];
  return latest?.url ? { url: latest.url, filename: latest.filename } : null;
};

/** レビュー値（仕様書 §3-2）。 */
export type DmReviewRating = "LIKE" | "DISLIKE" | "NO_ACTION";
export type DmReviewStatus = "OK" | "匿名ならOK" | "許可しない";

export interface DmReviewEntry {
  rating: DmReviewRating | null;
  note: string | null;
  status: DmReviewStatus | null;
  created_at: string | null;
}

/** subcontract かつ `status="completed"` のときのみ返される。 */
export interface DmReviewsBlock {
  by_requester: DmReviewEntry | null;
  by_contractor: DmReviewEntry | null;
}

/** レビュー投稿で選べる評価（NO_ACTION は投稿不可、Good/Bad 必須）。 */
export type DmReviewRatingInput = "LIKE" | "DISLIKE";

/**
 * `POST /api/me/<project_id>/dm/<dm_id>/review`
 *
 * オファー完了後（status="completed"）の相互レビュー投稿。当事者（委託者 / 受託者）
 * のみ、相手への 1 件を投稿する。同一ユーザーの再投稿は upsert（更新）。
 * 投稿者本人はサーバ側で request.user から特定される（なりすまし防止のため
 * user_id は送らない）。
 */
export interface SubmitDmReviewParams {
  project_id: string;
  dm_id: string;
  /** 全体評価（Good/Bad）。 */
  rating: DmReviewRatingInput;
  /** レビュー本文（必須・最大 1500 文字）。 */
  note: string;
  /** プロフィール掲載同意。 */
  status: DmReviewStatus;
}

export interface SubmitDmReviewResponse {
  status: string;
  data: {
    review: DmReviewEntry;
  };
}

/** Detail API のレスポンスデータ部 */
export interface DmThreadDetailData {
  thread: DmThread;
  offer_form: DmOfferForm | null;
  initial_files: DmInitialFile[];
  contract_files: DmContractFile[];
  /** subcontract かつ `status="completed"` のときのみキー返却 */
  reviews?: DmReviewsBlock;
}

export interface DmThreadDetailResponse {
  status: string;
  data: DmThreadDetailData;
}

export interface GetDmThreadsParams {
  project_id: string;
  type?: "subcontract" | "master_contract" | "all";
  status?: string;
  role?: "requester" | "contractor";
  search?: string;
  sort_by?: "modified" | "created";
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export interface GetDmThreadDetailParams {
  project_id: string;
  dm_id: string;
}

// ───── ドラフト作成 / 差分更新 / 削除 のパラメータ ─────

/**
 * `POST /api/me/<project_id>/dm/`
 *
 * 仕様書 §4 を参照。subcontract の作成は以下 2 通り（**排他**）:
 *
 *  - (A) 既存ユーザーへの直接オファー: `contractor_user_id` を指定
 *  - (B) メール経由オファー（受託者が未登録 / 確認前）: `recipient_email` を指定
 *
 * (A) と (B) を同時指定すると 400 が返る。`master_contract` ではどちらも無視される
 * （クライアント G → プロデューサー G の自動配信のため）。
 */
export interface CreateDmThreadParams {
  project_id: string;
  thread_type: DmThreadType;
  /** subcontract で既存ユーザーを指定するときに必須（recipient_email と排他） */
  contractor_user_id?: number;
  /** subcontract でメール経由オファーを起こすときに必須（contractor_user_id と排他） */
  recipient_email?: string;
  /**
   * メール経由オファーで任意指定する招待相手の表示名。
   * 未登録ユーザーの招待 AuthUser の暫定表示名（`stage_name`）に反映される。
   * `recipient_email` 指定時のみ有効。
   */
  recipient_username?: string;
}

export interface CreateDmThreadResponse {
  status: string;
  data: {
    thread: DmThread;
  };
}

/**
 * `PATCH /api/me/<project_id>/dm/<dm_id>` のリクエストボディ。
 *
 * 仕様書 §5 を参照。Detail API の各ブロック（`thread` / `offer_form`）で
 * 公開しているフィールドはすべて読み書き対称になるよう PATCH でも受け付ける。
 *
 * `thread_type` で意味を持たないフィールドはサーバ側で黙って無視（200 OK で返却）される。
 *
 * ### `accept_position` の扱い
 * - `PRODUCER` / `DIRECTOR` / `SPECIALIST` のいずれか、または `null` / `''`（空文字列）。
 *   `null` と `''` はサーバ側で等価扱い（「変更なし」）。
 * - 仕様書 §6 のグレース対応なし方針により、旧 lowercase 値（`producer` / `admin` / `creator`）
 *   を送ると 400 Bad Request になる。
 */
export interface UpdateDmThreadParams {
  project_id: string;
  dm_id: string;

  // thread ブロック
  assigned_roles?: string;
  scope?: string;
  contract_amount?: number | null;
  advance_payment_rate?: number;
  contract_start_date?: string | null;
  contract_end_date?: string | null;
  deadline?: string | null;
  is_transactional?: boolean;
  requires_deliverable?: boolean;
  /** subcontract のみ反映。master_contract は黙って無視 */
  allow_subcontracting?: boolean;
  /** subcontract のみ反映。master_contract は黙って無視 */
  accept_position?: AcceptPosition | null | "";
  initial_message?: string;
  status?: DmThreadStatus;

  // offer_form (subcontract)
  special_provisions?: string | null;
  quantity?: string | null;
  deliverable_format?: string | null;
  delivery_method?: string | null;
  delivery_location?: string | null;
  valid_until?: string | null;
  /** メール経由オファーの宛先メール。subcontract のみ。`null` / 空文字でクリア */
  recipient_email?: string | null;
  /**
   * 受託者（contractor）の再割り当て。subcontract のドラフト中のみ反映される
   * （`recipient_email` と排他）。同一 ID の再送は BE 側で no-op。
   * オファー作成ウィザードで送信先を選び直したケースを autosave で同期する。
   */
  contractor_user_id?: number;

  // offer_form (master_contract)
  proposed_budget?: number | null;
  contract_type?: MasterContractContractType;
  ownership_type?: MasterContractOwnershipType;
  disclosure_rule?: MasterContractDisclosureRule;

  // offer_form (両方)
  /**
   * 対価の内訳。non-empty で送ると BE が `contract_amount` を `Σ unit_price * quantity`
   * で自動計算・上書きする。空配列で送ると breakdown をクリアする（`contract_amount` は据え置き）。
   * 各要素は `label` / `unit_price` / `quantity` / `unit` の 4 キーすべてを含めること
   * （不足キーは BE 側で空文字 / 0 に正規化される）。`unit_price` / `quantity` は文字列・数値どちらも可。
   */
  compensation_lines?: PatchCompensationLine[];
}

/**
 * PATCH で送る対価行。Read のときと違い、`unit_price` / `quantity` は文字列でも数値でも受け付けられる。
 */
export interface PatchCompensationLine {
  label: string;
  unit_price: string | number;
  quantity: string | number;
  unit: string;
}

export interface UpdateDmThreadResponse {
  status: string;
  data: {
    thread: DmThread;
  };
}

/**
 * `DELETE /api/me/<project_id>/dm/<dm_id>`
 *
 * requester のみ実行可能。ドラフトでも実行可能（ソフト削除）。
 * 仕様書 §6 を参照。
 */
export interface DeleteDmThreadParams {
  project_id: string;
  dm_id: string;
}

// ───── 締結後の契約更新（受託者の承認ゲート） ─────

/**
 * `POST /api/me/<project_id>/dm/<dm_id>/amendment-request`
 *
 * 変更したいフィールドだけを送る。値の形式は `UpdateDmThreadParams` と同じ。
 * 現在値と同じ値しか含まない場合は BE が 400 を返す。
 * `AMENDABLE_API_FIELDS` 以外を含めても 400 になる。
 */
export interface ProposeContractAmendmentParams {
  project_id: string;
  dm_id: string;

  assigned_roles?: string;
  scope?: string;
  initial_message?: string;
  contract_amount?: number | null;
  compensation_lines?: PatchCompensationLine[];
  contract_start_date?: string | null;
  contract_end_date?: string | null;
  deadline?: string | null;
  quantity?: string | null;
  deliverable_format?: string | null;
  delivery_method?: string | null;
  delivery_location?: string | null;
  special_provisions?: string | null;
}

/** `DELETE /api/me/<project_id>/dm/<dm_id>/amendment-request`（委託者の取り下げ） */
export interface WithdrawContractAmendmentParams {
  project_id: string;
  dm_id: string;
}

/** `POST /api/me/<project_id>/dm/<dm_id>/amendment-response`（受託者の応答） */
export interface RespondContractAmendmentParams {
  project_id: string;
  dm_id: string;
  response: "accepted" | "rejected";
}

// ───── 過去オファー履歴（フォームプリフィル用） ─────

/**
 * `GET /api/me/offer-history` の1件分。
 *
 * 同じ相手 × 同じロール名で自分が過去に送信した subcontract オファー。
 * オファーウィザードの詳細フォームで「テンプレート / 過去オファー」の
 * プリフィル元切り替えに使う。日付系（納期・有効期限・契約期間）は
 * 過去日付の混入を避けるため BE から返さない（テンプレートの相対計算を使う）。
 */
export interface OfferHistoryItem {
  dm_id: string;
  project: {
    id: string | null;
    name: string | null;
  };
  status: DmThreadStatus;
  created_at: string;
  updated_at: string;
  // --- フォーム反映用の内容フィールド ---
  assigned_roles: string | null;
  scope: string;
  initial_message: string | null;
  /** 契約金額（税抜・整数円）。表示時は `addConsumptionTax()` で税込変換する。 */
  contract_amount: number | null;
  compensation_lines: ApiCompensationLine[];
  quantity: string | null;
  deliverable_format: string | null;
  requires_deliverable: boolean;
  special_provisions: string | null;
  delivery_method: string | null;
  delivery_location: string | null;
  allow_subcontracting: boolean;
  accept_position: AcceptPosition | null;
  advance_payment_rate: number;
}

export interface OfferHistoryResponse {
  status: string;
  data: {
    count: number;
    offers: OfferHistoryItem[];
  };
}

/** 宛先はユーザー ID（フォロー経路）かメールアドレス（招待経路）のどちらか必須。 */
export interface GetOfferHistoryParams {
  role_name: string;
  contractor_user_id?: number;
  recipient_email?: string;
  limit?: number;
}

// 進行中のステータス一覧
// 注: ``draft`` / ``new`` は subcontract のドラフト状態。BE は新 API では ``draft`` を
// 返すが、後方互換のため両方を含める（``isOfferDraftStatus`` も同様に両対応）。
export const IN_PROGRESS_STATUSES: DmThreadStatus[] = [
  "draft",
  "new",
  "estimating",
  "estimate_review",
  "contractor_pending",
  "in_progress",
  "inspection_pending",
];

// 完了系のステータス一覧
export const COMPLETED_STATUSES: DmThreadStatus[] = [
  "completed",
  "closed",
  "rejected",
];

/** 1 リクエストあたりの取得件数。BE の `DmThreadPagination.max_limit` と同値。 */
const THREADS_PAGE_LIMIT = 100;
/** 全ページ取得の安全上限（無限ループ防止。100 件 × 20 ページ = 2000 件） */
const THREADS_MAX_PAGES = 20;
/** 2 ページ目以降をまとめて取りに行くときの同時リクエスト数 */
const THREADS_FETCH_CONCURRENCY = 4;

/** フィルタ条件とページ指定から一覧 API の URL を組み立てる。 */
export const buildDmThreadsUrl = (
  { project_id, type, status, role, search, sort_by, order }: GetDmThreadsParams,
  page: { limit?: number; offset?: number } = {},
): string => {
  const params: Record<string, string> = {};
  if (type) params.type = type;
  if (status) params.status = status;
  if (role) params.role = role;
  if (search) params.search = search;
  if (sort_by) params.sort_by = sort_by;
  if (order) params.order = order;
  if (page.limit !== undefined) params.limit = page.limit.toString();
  if (page.offset !== undefined) params.offset = page.offset.toString();

  const queryString = new URLSearchParams(params).toString();
  const qs = queryString ? `?${queryString}` : "";
  return `/api/me/${project_id}/dm/${qs}`;
};

/** `queryFn` の `fetchWithBQ` のうち、ページ取得で使う部分だけを写した型。 */
type PageResult = { data?: unknown; error?: FetchBaseQueryError };
type FetchPage = (url: string) => PageResult | PromiseLike<PageResult>;

type DmThreadsQueryResult =
  | { data: DmThreadsResponse; error?: undefined }
  | { data?: undefined; error: FetchBaseQueryError };

/**
 * DM スレッド一覧を全ページ取得して 1 つのレスポンスに畳む。
 *
 * BE は既定 20 件・最大 100 件でページネーションするため、1 回だけ叩くと
 * オファー数の多いプロジェクトで古いスレッドが一覧から消える。DM の左カラムは
 * 進行中 / 完了の振り分けも検索もクライアント側で行う前提なので、ここで全件を
 * 揃えてから返す。1 ページ目の `count` で総数が分かるので、残りは `has_next` を
 * 直列に辿らずオフセットを算出して並列に取得する。
 */
export const fetchAllDmThreadPages = async (
  args: GetDmThreadsParams,
  fetchPage: FetchPage,
): Promise<DmThreadsQueryResult> => {
  const pageLimit = args.limit ?? THREADS_PAGE_LIMIT;
  const getPage = (offset: number) =>
    fetchPage(buildDmThreadsUrl(args, { limit: pageLimit, offset }));

  const firstResult = await getPage(0);
  if (firstResult.error) return { error: firstResult.error };

  const firstData = (firstResult.data as DmThreadsResponse).data;
  const allThreads: DmThread[] = [...(firstData.threads || [])];

  // BE が limit をクランプした場合にも追従できるよう、応答の limit を使う。
  const effectiveLimit = firstData.limit || pageLimit;
  const totalPages = Math.min(
    Math.ceil((firstData.count || allThreads.length) / effectiveLimit),
    THREADS_MAX_PAGES,
  );
  const restOffsets = Array.from(
    { length: Math.max(0, totalPages - 1) },
    (_, index) => (index + 1) * effectiveLimit,
  );

  const results = await mapWithConcurrency(
    restOffsets,
    THREADS_FETCH_CONCURRENCY,
    (offset) => Promise.resolve(getPage(offset)),
  );
  for (const result of results) {
    if (result.error) return { error: result.error };
    allThreads.push(
      ...((result.data as DmThreadsResponse).data.threads || []),
    );
  }

  // 全件返したときは呼び出し側が誤って追加取得しないよう潰す。
  // `THREADS_MAX_PAGES` で打ち切ったときだけ続きがあることを残す。
  const nextOffset = totalPages * effectiveLimit;
  const isTruncated = nextOffset < (firstData.count || allThreads.length);

  return {
    data: {
      status: "success",
      data: {
        ...firstData,
        threads: allThreads,
        has_next: isTruncated,
        next_offset: isTruncated ? nextOffset : null,
        limit: effectiveLimit,
        offset: 0,
      },
    },
  };
};

export const dmThreadsApi = createApi({
  reducerPath: "dmThreadsApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["DmThreads", "DmThreadDetail"],
  endpoints: (builder) => ({
    getDmThreads: builder.query<DmThreadsResponse, GetDmThreadsParams>({
      queryFn: async (args, _api, _extraOptions, fetchWithBQ) => {
        // limit / offset の明示は呼び出し側がページサイズを決めている意図なので
        // （件数判定や上限付きの表示）、そのまま 1 ページだけ返す。
        if (args.limit !== undefined || args.offset !== undefined) {
          const result = await fetchWithBQ(
            buildDmThreadsUrl(args, { limit: args.limit, offset: args.offset }),
          );
          if (result.error) return { error: result.error };
          return { data: result.data as DmThreadsResponse };
        }
        return fetchAllDmThreadPages(args, fetchWithBQ);
      },
      providesTags: ["DmThreads"],
      keepUnusedDataFor: 30,
    }),

    getDmThreadDetail: builder.query<DmThreadDetailResponse, GetDmThreadDetailParams>({
      query: ({ project_id, dm_id }) =>
        `/api/me/${project_id}/dm/${dm_id}`,
      providesTags: (_result, _error, arg) => [
        { type: "DmThreadDetail", id: arg.dm_id },
      ],
      keepUnusedDataFor: 60,
    }),

    /**
     * ドラフト DM スレッドを作成する（status: "new"）。
     * 仕様書 §4: 受信者選択直後に呼ぶことで、以降のステップ入力を debounce PATCH で
     * 永続化できるようになる。
     */
    createDmThread: builder.mutation<CreateDmThreadResponse, CreateDmThreadParams>({
      query: ({
        project_id,
        thread_type,
        contractor_user_id,
        recipient_email,
        recipient_username,
      }) => ({
        url: `/api/me/${project_id}/dm/`,
        method: "POST",
        body: {
          thread_type,
          ...(contractor_user_id !== undefined && { contractor_user_id }),
          ...(recipient_email !== undefined && { recipient_email }),
          ...(recipient_username !== undefined &&
            recipient_username !== "" && { recipient_username }),
        },
      }),
      invalidatesTags: ["DmThreads"],
    }),

    /**
     * DM スレッドの差分更新 + 状態遷移。仕様書 §5。
     * - ドラフト中はフォーム差分の自動保存（debounce）に使う
     * - 送信時は `{ status: "contractor_pending" }`、引受時は `{ status: "in_progress" }` を送る
     */
    updateDmThread: builder.mutation<UpdateDmThreadResponse, UpdateDmThreadParams>({
      query: ({ project_id, dm_id, ...patch }) => ({
        url: `/api/me/${project_id}/dm/${dm_id}`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: (_result, _error, arg) => [
        "DmThreads",
        { type: "DmThreadDetail", id: arg.dm_id },
      ],
      // ステータス遷移 / 成立後の金額編集で残高（CurrentUser）と進行中一覧
      // （Transactions）が変動するため、別 API スライスのキャッシュも無効化する。
      // ドラフト中の autosave（status 無し・成立前のフィールド編集）では発火しない。
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const isAmountEdit =
          arg.contract_amount !== undefined ||
          arg.compensation_lines !== undefined;
        if (!arg.status && !isAmountEdit) return;
        try {
          const { data } = await queryFulfilled;
          const resultStatus = data?.data?.thread?.status;
          // 金額編集は成立状態（in_progress / inspection_pending）のときだけ
          // escrow が動くため、それ以外（ドラフト編集）は無効化しない。
          if (!arg.status && !ESCROW_ACTIVE_STATUSES.has(resultStatus ?? "")) {
            return;
          }
          invalidateOfferAccountCaches(dispatch);
        } catch {
          /* 失敗時は残高に影響しないため何もしない */
        }
      },
    }),

    /**
     * 締結後の契約更新を提案する（委託者のみ）。
     * `POST /api/me/<project_id>/dm/<dm_id>/amendment-request`
     *
     * 契約はこの時点では変わらない。受託者が `respondContractAmendment` で
     * 承認して初めて反映される。同時に開ける提案は 1 件で、再送は上書き。
     */
    proposeContractAmendment: builder.mutation<
      UpdateDmThreadResponse,
      ProposeContractAmendmentParams
    >({
      query: ({ project_id, dm_id, ...changes }) => ({
        url: `/api/me/${project_id}/dm/${dm_id}/amendment-request`,
        method: "POST",
        body: changes,
      }),
      invalidatesTags: (_result, _error, arg) => [
        "DmThreads",
        { type: "DmThreadDetail", id: arg.dm_id },
      ],
    }),

    /**
     * 提案中の契約更新を取り下げる（委託者のみ）。
     * `DELETE /api/me/<project_id>/dm/<dm_id>/amendment-request`
     */
    withdrawContractAmendment: builder.mutation<
      UpdateDmThreadResponse,
      WithdrawContractAmendmentParams
    >({
      query: ({ project_id, dm_id }) => ({
        url: `/api/me/${project_id}/dm/${dm_id}/amendment-request`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        "DmThreads",
        { type: "DmThreadDetail", id: arg.dm_id },
      ],
    }),

    /**
     * 契約更新の提案に承認 / 却下を返す（受託者のみ）。
     * `POST /api/me/<project_id>/dm/<dm_id>/amendment-response`
     *
     * 承認時のみ escrow が動く（増額なら lock + release、減額なら decrease）ため、
     * そのときだけ残高系キャッシュを無効化する。却下では金銭は動かない。
     */
    respondContractAmendment: builder.mutation<
      UpdateDmThreadResponse,
      RespondContractAmendmentParams
    >({
      query: ({ project_id, dm_id, response }) => ({
        url: `/api/me/${project_id}/dm/${dm_id}/amendment-response`,
        method: "POST",
        body: { response },
      }),
      invalidatesTags: (_result, _error, arg) => [
        "DmThreads",
        { type: "DmThreadDetail", id: arg.dm_id },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        if (arg.response !== "accepted") return;
        try {
          await queryFulfilled;
          invalidateOfferAccountCaches(dispatch);
        } catch {
          /* 失敗時は残高に影響しないため何もしない */
        }
      },
    }),

    /**
     * DM スレッドのソフト削除（ドラフト破棄を含む）。仕様書 §6。
     */
    deleteDmThread: builder.mutation<void, DeleteDmThreadParams>({
      query: ({ project_id, dm_id }) => ({
        url: `/api/me/${project_id}/dm/${dm_id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        "DmThreads",
        { type: "DmThreadDetail", id: arg.dm_id },
      ],
    }),

    /**
     * 同じ相手 × 同じロール名で過去に送信したオファーの履歴（全プロジェクト横断）。
     * オファーウィザード詳細フォームのプリフィル元カード表示に使う。
     */
    getOfferHistory: builder.query<OfferHistoryResponse, GetOfferHistoryParams>({
      query: ({ role_name, contractor_user_id, recipient_email, limit }) => {
        const params: Record<string, string> = { role_name };
        if (contractor_user_id !== undefined) {
          params.contractor_user_id = contractor_user_id.toString();
        }
        if (recipient_email) params.recipient_email = recipient_email;
        if (limit !== undefined) params.limit = limit.toString();
        return `/api/me/offer-history?${new URLSearchParams(params).toString()}`;
      },
      keepUnusedDataFor: 60,
    }),

    /**
     * オファー完了後の相互レビュー投稿（status="completed" のときのみ）。
     * 成功時は Detail を無効化し、`reviews` ブロックを再取得して反映する。
     */
    submitDmReview: builder.mutation<SubmitDmReviewResponse, SubmitDmReviewParams>({
      query: ({ project_id, dm_id, rating, note, status }) => ({
        url: `/api/me/${project_id}/dm/${dm_id}/review`,
        method: "POST",
        body: { rating, note, status },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "DmThreadDetail", id: arg.dm_id },
      ],
    }),
  }),
});

export const {
  useGetDmThreadsQuery,
  useLazyGetDmThreadsQuery,
  useGetDmThreadDetailQuery,
  useLazyGetDmThreadDetailQuery,
  useCreateDmThreadMutation,
  useUpdateDmThreadMutation,
  useDeleteDmThreadMutation,
  useProposeContractAmendmentMutation,
  useWithdrawContractAmendmentMutation,
  useRespondContractAmendmentMutation,
  useGetOfferHistoryQuery,
  useSubmitDmReviewMutation,
} = dmThreadsApi;

// ───── 型ガード ─────

/** Detail API の `offer_form` が subcontract バリアントかどうか。 */
export const isSubcontractOfferForm = (
  form: DmOfferForm | null | undefined,
  threadType: DmThreadType,
): form is SubcontractOfferForm => {
  return !!form && threadType === "subcontract";
};

/** Detail API の `offer_form` が master_contract バリアントかどうか。 */
export const isMasterContractOfferForm = (
  form: DmOfferForm | null | undefined,
  threadType: DmThreadType,
): form is MasterContractOfferForm => {
  return !!form && threadType === "master_contract";
};
